import { computed, makeObservable } from 'mobx'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { Cubit } from './core'
import { fetchSlideDeckRoles } from '../firestore/SlideDeckRole'
import { SlideDeckRole } from '../models/SlideDeckRole'
import { type StaticModelCollection } from '../types'
import { getGroupSizeConstraintsFromRoles } from '../sources-of-truth/getGroupSizeConstraintsFromRoles'

export class CreateOrEditAssignmentCubit extends Cubit {
  repository: FirebaseRepository
  slideDeckId: string
  roles: StaticModelCollection<SlideDeckRole>

  constructor({
    repository,
    slideDeckId,
    roles,
  }: {
    repository: FirebaseRepository
    slideDeckId: string
    roles?: SlideDeckRole[]
  }) {
    super()
    this.repository = repository
    this.slideDeckId = slideDeckId
    this.roles = SlideDeckRole.emptyCollection(repository)

    if (roles) this.roles.replaceModels(roles)

    makeObservable(this)
  }

  async initialize(): Promise<void> {
    if (this.roles.isLoaded) return

    const roles = await fetchSlideDeckRoles(this.repository, {
      slideDeckId: this.slideDeckId,
    })

    if (this.isDisposed) return

    this.roles.replaceModels(roles)
  }

  @computed
  get ready() {
    // undefined == still loading
    return this.roles.isLoaded
  }

  /**
   * returns `null` if no role constraints for slide deck
   */
  @computed
  get roleGroupSizeConstraints() {
    // no roles case, if null return we can defer to default flow
    if (!this.roles.models.length) return null

    const { min: minGroupSize, max: maxGroupSize } =
      getGroupSizeConstraintsFromRoles(this.roles.models)

    return { minGroupSize, maxGroupSize }
  }
}
